// ═══ ACTIVE ORDERS STRIP — pending/open orders + swipe-left to cancel ═════
// Iter 97. Mounts inside the home grid above "LIVE POSITIONS". Calls
// /api/orders/active every 8s. Each row is swipe-to-cancel (haptic confirm).
//
// Empty state: renders nothing (strip disappears when no active orders).
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { hapticTap, hapticWarning, hapticSuccess } from "../../utils/haptics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status colors mirror the /orders/active backend contract.
const STATUS_COLORS = {
  NEW: "#fbbf24",
  PENDING: "#fbbf24",
  SUBMITTED: "#fbbf24",
  PARTIAL: "#60a5fa",
  PARTIALLY_FILLED: "#60a5fa",
  QUEUED: "#a78bfa",
  UNKNOWN: "#8b949e",
};

export function ActiveOrdersStrip() {
  const [orders, setOrders] = useState([]);
  const [cancelling, setCancelling] = useState({}); // order_id → bool
  const [toasts, setToasts] = useState([]); // transient fill notifications
  const prevMapRef = useRef({}); // order_id → last known status (for delta detection)

  const pushToast = (toast) => {
    const id = toast.id || Math.random().toString(36).slice(2);
    setToasts(t => [...t, { ...toast, id }]);
    // Auto-dismiss after 5 s.
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 5000);
    try { hapticSuccess && hapticSuccess(); } catch {}
  };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/orders/active`);
      if (!r.data?.ok) return;
      const incoming = r.data.active || [];

      // iter 98 — fill / partial-fill detection via status delta between polls.
      // For any row that was present last poll with a non-terminal status and
      // is now missing from incoming OR has flipped to FILLED/PARTIALLY_FILLED,
      // fire a toast. Most FILLED rows disappear from /active entirely since
      // the endpoint filters out terminal statuses — we catch those via
      // "was in prevMap, not in incoming".
      const incomingMap = Object.fromEntries(incoming.map(o => [o.order_id, o]));
      for (const [id, prevStatus] of Object.entries(prevMapRef.current)) {
        const cur = incomingMap[id];
        // If vanished entirely (and previously not terminal) → check with the
        // orders/recent fallback to learn its terminal state.
        if (!cur) {
          if (prevStatus && !/FILL|CANCEL|REJECT|EXPIR/i.test(prevStatus)) {
            // Fire-and-forget recent-orders fetch to classify what happened.
            axios.get(`${API}/orders/recent`).then(rr => {
              const row = (rr.data?.orders || []).find(o => o.order_id === id);
              if (!row) return;
              const st = String(row.status || "").toUpperCase();
              if (st === "FILLED") pushToast({ kind: "filled", symbol: row.symbol, side: row.side, quantity: row.quantity, limitPrice: row.limitPrice });
              else if (st === "CANCELLED" || st === "CANCELED") { /* user-driven, skip */ }
              else if (st === "REJECTED") pushToast({ kind: "rejected", symbol: row.symbol, side: row.side });
            }).catch(() => {});
          }
        } else {
          const curSt = String(cur.status || "").toUpperCase();
          // Partial → Filled or New → Partial transitions are visible via delta.
          if (curSt === "PARTIALLY_FILLED" && prevStatus !== "PARTIALLY_FILLED") {
            pushToast({ kind: "partial", symbol: cur.symbol, side: cur.side, filled: cur.filledQuantity, total: cur.quantity });
          }
        }
      }

      prevMapRef.current = Object.fromEntries(incoming.map(o => [o.order_id, String(o.status || "").toUpperCase()]));
      setOrders(incoming);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    // iter 98 — tighter poll (5 s) when we have any orders so fill transitions
    // flash fast; drops back to 8 s when nothing is pending.
    const tick = () => load();
    let iv = setInterval(tick, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancel = async (o) => {
    hapticWarning && hapticWarning();
    setCancelling(s => ({ ...s, [o.order_id]: true }));
    try {
      const r = await axios.post(`${API}/orders/${encodeURIComponent(o.order_id)}/cancel`);
      if (r.data?.ok && r.data.cancelled) {
        hapticSuccess && hapticSuccess();
        setOrders(prev => prev.filter(x => x.order_id !== o.order_id));
      } else {
        setCancelling(s => ({ ...s, [o.order_id]: false }));
      }
    } catch {
      setCancelling(s => ({ ...s, [o.order_id]: false }));
    }
  };

  if (!orders || orders.length === 0) {
    // Even with no active orders we may still have pending toasts to flash.
    return toasts.length > 0 ? <FillToastStack toasts={toasts} onDismiss={(id) => setToasts(t => t.filter(x => x.id !== id))} /> : null;
  }

  return (
    <>
      <div data-testid="active-orders-strip" style={{
        margin: "0 18px 12px", padding: "14px 16px",
        background: "rgba(251,191,36,0.04)",
        border: "1px solid rgba(251,191,36,0.18)",
        borderRadius: 18,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fbbf24", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#fbbf24" }}>
            Active Orders
          </span>
          <span style={{ fontSize: 10, color: "var(--muted)", marginLeft: "auto" }}>
            {orders.length} pending · swipe ← to cancel
          </span>
        </div>
        {orders.map(o => (
          <ActiveOrderRow
            key={o.order_id}
            o={o}
            cancelling={!!cancelling[o.order_id]}
            onCancel={() => cancel(o)}
          />
        ))}
      </div>
      <FillToastStack toasts={toasts} onDismiss={(id) => setToasts(t => t.filter(x => x.id !== id))} />
    </>
  );
}

// ─── Toast stack for fill/partial-fill notifications (iter 98) ──────────────
function FillToastStack({ toasts, onDismiss }) {
  return (
    <div data-testid="fill-toast-stack" style={{
      position: "fixed", top: 18, left: 0, right: 0, zIndex: 20030,
      display: "flex", flexDirection: "column", gap: 8,
      alignItems: "center", pointerEvents: "none",
    }}>
      {toasts.map(t => {
        const color = t.kind === "rejected" ? "#f87171" : t.kind === "partial" ? "#60a5fa" : "#4ade80";
        const emoji = t.kind === "rejected" ? "✕" : t.kind === "partial" ? "◐" : "✓";
        const title =
          t.kind === "filled"   ? `Order filled` :
          t.kind === "partial"  ? `Partial fill` :
          t.kind === "rejected" ? `Order rejected` : "Order update";
        const body =
          t.kind === "filled"   ? `${t.side} ${t.quantity} ${t.symbol}${t.limitPrice ? ` @ $${Number(t.limitPrice).toFixed(2)}` : ""}` :
          t.kind === "partial"  ? `${t.filled || 0}/${t.total || 0} filled on ${t.side} ${t.symbol}` :
          t.kind === "rejected" ? `${t.side} ${t.symbol} — please retry with a fresh limit` : "";
        return (
          <div key={t.id}
            data-testid={`fill-toast-${t.kind}`}
            onClick={() => onDismiss(t.id)}
            style={{
              pointerEvents: "auto",
              width: "min(360px, 92vw)",
              padding: "12px 14px",
              borderRadius: 14,
              background: "rgba(13,17,23,0.96)",
              backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
              border: `1px solid ${color}55`,
              boxShadow: `0 12px 36px ${color}22, 0 0 0 1px rgba(255,255,255,0.05)`,
              display: "flex", alignItems: "center", gap: 12,
              animation: "fillToastIn 260ms cubic-bezier(.2,.9,.3,1.1)",
              cursor: "pointer",
            }}
          >
            <span style={{
              width: 28, height: 28, borderRadius: "50%",
              background: `${color}20`, border: `1px solid ${color}`,
              display: "grid", placeItems: "center",
              fontSize: 14, fontWeight: 800, color,
            }}>{emoji}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color, letterSpacing: 1 }}>{title.toUpperCase()}</div>
              <div style={{ fontSize: 12, color: "#e6edf3", marginTop: 1 }}>{body}</div>
            </div>
            <span style={{ fontSize: 9, color: "#636e7b" }}>tap to dismiss</span>
          </div>
        );
      })}
      <style>{`
        @keyframes fillToastIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
}


function ActiveOrderRow({ o, cancelling, onCancel }) {
  const [dx, setDx] = useState(0);
  const startRef = useRef({ x: 0, tracking: false, startDx: 0 });
  const ACTION_W = 90;

  const onPointerDown = (e) => { startRef.current = { x: e.clientX, tracking: true, startDx: dx }; };
  const onPointerMove = (e) => {
    if (!startRef.current.tracking) return;
    const next = Math.max(-ACTION_W, Math.min(0, startRef.current.startDx + (e.clientX - startRef.current.x)));
    setDx(next);
  };
  const onPointerUp = () => {
    if (!startRef.current.tracking) return;
    startRef.current.tracking = false;
    // Snap open if past 50% or snap closed.
    if (dx < -ACTION_W / 2) {
      setDx(-ACTION_W);
      hapticTap();
    } else {
      setDx(0);
    }
  };

  const status = String(o.status || "UNKNOWN").toUpperCase();
  const statusColor = STATUS_COLORS[status] || STATUS_COLORS.UNKNOWN;
  const sideColor = o.side === "BUY" ? "#4ade80" : "#f87171";
  const isCrypto = String(o.type || "").toUpperCase() === "CRYPTO";
  const isOption = String(o.type || "").toUpperCase() === "OPTION";

  // Simplified display symbol — for options, the raw OSI is ugly.
  let display = o.symbol || "—";
  if (isOption && display.length >= 15) {
    // OSI pattern: TICKER YYMMDD C/P 00000000 (may have spaces or not)
    const clean = display.replace(/\s+/g, "");
    const m = clean.match(/^([A-Z]+)(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
    if (m) display = `${m[1]} $${parseInt(m[6], 10) / 1000}${m[5]}`;
  }

  return (
    <div style={{ position: "relative", overflow: "hidden", marginBottom: 6 }}>
      {/* Cancel button revealed on swipe */}
      <div style={{
        position: "absolute", top: 0, right: 0, bottom: 0, width: ACTION_W,
        display: "flex", alignItems: "center", justifyContent: "center",
        pointerEvents: dx < -20 ? "auto" : "none",
      }}>
        <button
          data-testid={`active-order-cancel-${o.order_id}`}
          onClick={(e) => { e.stopPropagation(); onCancel(); }}
          disabled={cancelling}
          style={{
            width: ACTION_W, height: "100%", border: "none", cursor: cancelling ? "wait" : "pointer",
            background: "rgba(239,68,68,0.22)", color: "#f87171",
            fontSize: 11, fontWeight: 800, letterSpacing: 1,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          }}
        >
          {cancelling ? "…" : <><span style={{ fontSize: 16, marginBottom: 2 }}>×</span>CANCEL</>}
        </button>
      </div>

      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid={`active-order-${o.order_id}`}
        style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 2px",
          background: "var(--bg)",
          transform: `translateX(${dx}px)`,
          transition: startRef.current.tracking ? "none" : "transform 220ms cubic-bezier(.2,.9,.3,1)",
          touchAction: "pan-y",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 9, fontWeight: 800, color: sideColor, padding: "2px 6px", borderRadius: 4, background: `${sideColor}18`, letterSpacing: 1 }}>
          {o.side}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {o.quantity} × {display}
          </div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 1 }}>
            {isCrypto ? "crypto" : isOption ? "option" : "stock"}
            {o.limitPrice ? ` · lim $${Number(o.limitPrice).toFixed(2)}` : ""}
            {o.source === "retry_queue" && o.attempts != null ? ` · retry ${o.attempts}` : ""}
          </div>
          {o.smartStatus && (
            <div
              data-testid={`active-order-smart-${o.order_id}`}
              style={{
                fontSize: 10, color: "#fbbf24", marginTop: 3,
                fontStyle: "italic", lineHeight: 1.3,
                overflow: "hidden", textOverflow: "ellipsis",
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              }}
            >
              {o.smartStatus}
            </div>
          )}
        </div>
        <span style={{
          fontSize: 9, fontWeight: 800, color: statusColor,
          padding: "3px 7px", borderRadius: 5, background: `${statusColor}22`, letterSpacing: 1,
        }}>
          {status.replace("PARTIALLY_FILLED", "PARTIAL")}
        </span>
      </div>
    </div>
  );
}
